import { PdfPageCanvas } from '../../PdfCanvas/PdfPageCanvas.tsx';
import { PlacementOverlay } from './PlacementOverlay.tsx';
import { usePlacementCanvas } from './PlacementCanvas.hooks.ts';
import type { CanvasPlacement, FixedPlacement, PdfBox, SlotMeta } from './PlacementCanvas.types.ts';

interface Props {
	file: Blob | string;
	scale: number;
	placements: Map<string, CanvasPlacement>;
	selectedSlotKey: string | null;
	slotByKey: Map<string, SlotMeta>;
	showMetadata: boolean;
	/** Other parties' boxes, drawn read-only and kept clear of. */
	fixedPlacements?: FixedPlacement[];
	onSelect: (slotKey: string) => void;
	onPlacementChange: (slotKey: string, pageNum: number, pdfBox: PdfBox) => void;
	/** Fires when the most-centrally-visible page changes (drives "place on
	 *  the visible page" UX); also fires once after pages mount. */
	onVisiblePageChange?: (pageNum: number) => void;
	/** Fires once on PDF load with the total page count, for the "Page X / Y" indicator. */
	onTotalPagesChange?: (totalPages: number) => void;
	/** When provided, each placed box shows a small × to remove it. */
	onRemove?: (slotKey: string) => void;
}

const NO_FIXED_PLACEMENTS: FixedPlacement[] = [];

/**
 * The placement surface on its own — one document, stamp boxes over it.
 *
 * The document and its pages come from `PdfPageCanvas`, which the binding surface uses too.
 */
export const PlacementCanvas = ({
	file,
	scale,
	placements,
	selectedSlotKey,
	slotByKey,
	showMetadata,
	fixedPlacements = NO_FIXED_PLACEMENTS,
	onSelect,
	onPlacementChange,
	onVisiblePageChange,
	onTotalPagesChange,
	onRemove,
}: Props) => {
	const {
		pageDims,
		placementsByPage,
		onPageRenderSuccess,
		scrollHostRef,
		pageRefs,
		collectPages,
		overlappingKeys,
		obstaclesOnPage,
	} = usePlacementCanvas({
		placements,
		selectedSlotKey,
		scale,
		slotByKey,
		showMetadata,
		fixedPlacements,
		onVisiblePageChange,
		onPlacementChange,
	});

	return (
		<PdfPageCanvas
			file={file}
			scale={scale}
			pageDims={pageDims}
			onPageRenderSuccess={onPageRenderSuccess}
			{...(onTotalPagesChange === undefined ? {} : { onTotalPagesChange })}
			scrollHostRef={scrollHostRef}
			pageRefs={pageRefs}
			testId="placement-canvas"
			pageTestId={(pageNum) => `placement-canvas-page-${pageNum}`}
			errorText="Failed to load PDF"
			noDataText="No PDF file"
			renderOverlay={({ pageNum, dims }) => (
				<PlacementOverlay
					pageNum={pageNum}
					dims={dims}
					scale={scale}
					placements={placementsByPage.get(pageNum) ?? []}
					fixedPlacements={fixedPlacements.filter((fixed) => fixed.pageNum === pageNum)}
					overlappingKeys={overlappingKeys}
					slotByKey={slotByKey}
					selectedSlotKey={selectedSlotKey}
					showMetadata={showMetadata}
					interactive
					collectPages={collectPages}
					obstaclesOnPage={obstaclesOnPage}
					onSelect={onSelect}
					onPlacementChange={onPlacementChange}
					{...(onRemove === undefined ? {} : { onRemove })}
				/>
			)}
		/>
	);
};
